/**
 * The project list, and one project.
 *
 * Both are deliberately plain. A project is a container — the work happens in its packages,
 * and the fastest path from here to there is what these screens are for.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { Envelope, PackageSummary, ProjectDetail, ProjectSummary } from '@/api/types'
import { navigate } from '@/app/routes'
import { Icon } from '@/components/Icon'
import { Empty, Failed, Skeleton } from '@/components/states'
import { formatDate, formatMoney } from '@/lib/format'

const PAGE = 25

export function ProjectList() {
  const { t, i18n } = useTranslation()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const projects = useQuery({
    queryKey: ['projects', search, offset],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) })
      if (search.trim()) params.set('search', search.trim())
      return api.get<Envelope<ProjectSummary>>(`/projects?${params}`, signal)
    },
    placeholderData: keepPreviousData,
  })

  if (projects.isError) {
    const error = projects.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void projects.refetch()}
        />
      </main>
    )
  }

  return (
    <main className="page" data-testid="projects">
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('nav.projects')}</h1>
          {projects.data ? (
            <p className="page__subtitle numeric">
              {t('projects.count', { count: projects.data.meta.total })}
            </p>
          ) : null}
        </div>
        <label className="grid__searchWrap">
          <Icon name="search" size={15} className="grid__searchIcon" />
          <input
            className="grid__search"
            type="search"
            value={search}
            placeholder={t('projects.search')}
            aria-label={t('projects.search')}
            onChange={(event) => {
              setSearch(event.target.value)
              setOffset(0)
            }}
            data-testid="projects-search"
          />
        </label>
      </header>

      {projects.isPending ? (
        <Skeleton rows={6} />
      ) : projects.data.items.length === 0 ? (
        <Empty title={t('projects.none', { term: search })} />
      ) : (
        <div className="grid__scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('projects.number')}</th>
                <th scope="col">{t('projects.title')}</th>
                <th scope="col">{t('projects.status')}</th>
                <th scope="col" className="table__numeric">
                  {t('projects.budget')}
                </th>
                <th scope="col">{t('projects.finish')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.data.items.map((project) => (
                <tr key={project.id} data-testid="project-row">
                  <td className="numeric">
                    <button
                      type="button"
                      className="link"
                      onClick={() => navigate({ name: 'project', projectId: project.id })}
                      data-testid="project-open"
                    >
                      {project.dof_project_number}
                    </button>
                  </td>
                  <td>{project.title}</td>
                  <td>
                    <span className={`chip chip--${project.status}`}>
                      {t(`status.${project.status}`, project.status)}
                    </span>
                  </td>
                  <td className="table__numeric numeric">
                    {formatMoney(project.revised_budget ?? project.original_budget, i18n.language)}
                  </td>
                  <td>{formatDate(project.planned_finish, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {projects.data && projects.data.meta.total > PAGE ? (
        <div className="grid__pager">
          <button
            type="button"
            className="button button--quiet"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            {t('grid.previous')}
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={!projects.data.meta.has_more}
            onClick={() => setOffset(offset + PAGE)}
          >
            {t('grid.next')}
          </button>
        </div>
      ) : null}
    </main>
  )
}

export function Project({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation()

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: ({ signal }) => api.get<ProjectDetail>(`/projects/${projectId}`, signal),
  })

  const packages = useQuery({
    queryKey: ['project', projectId, 'packages'],
    queryFn: ({ signal }) =>
      api.get<Envelope<PackageSummary>>(
        `/projects/${projectId}/packages?limit=200&sort=manual_package_id`,
        signal,
      ),
    enabled: project.isSuccess,
  })

  if (project.isError) {
    const error = project.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void project.refetch()}
        />
      </main>
    )
  }

  if (project.isPending) {
    return (
      <main className="page" data-testid="project">
        <Skeleton rows={5} />
      </main>
    )
  }

  const detail = project.data
  return (
    <main className="page" data-testid="project">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">
            <button type="button" className="link" onClick={() => navigate({ name: 'projects' })}>
              {t('nav.projects')}
            </button>
          </p>
          <h1 className="page__title">{detail.title}</h1>
          <p className="page__subtitle numeric">{detail.dof_project_number}</p>
        </div>
        <span className={`chip chip--${detail.status}`}>
          {t(`status.${detail.status}`, detail.status)}
        </span>
      </header>

      <section className="figures">
        <Figure
          label={t('projects.budget')}
          value={formatMoney(detail.revised_budget ?? detail.original_budget, i18n.language)}
        />
        <Figure label={t('package.plannedStart')} value={formatDate(detail.planned_start, i18n.language)} />
        <Figure label={t('package.plannedFinish')} value={formatDate(detail.planned_finish, i18n.language)} />
        <Figure label={t('nav.packages')} value={String(detail.package_count)} />
      </section>

      <section className="panel" data-testid="project-packages">
        <h2 className="panel__title">{t('nav.packages')}</h2>
        {packages.isPending ? (
          <Skeleton rows={4} />
        ) : !packages.data || packages.data.items.length === 0 ? (
          <Empty title={t('project.noPackages')} />
        ) : (
          <div className="grid__scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('package.id')}</th>
                  <th scope="col">{t('package.name')}</th>
                  <th scope="col">{t('package.status')}</th>
                  <th scope="col" className="table__numeric">
                    {t('package.budget')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {packages.data.items.map((pkg) => (
                  <tr key={pkg.id} data-testid="package-row">
                    <td className="numeric">
                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          navigate({ name: 'package', packageId: pkg.id, tab: 'summary' })
                        }
                        data-testid="package-open"
                      >
                        {pkg.manual_package_id}
                      </button>
                    </td>
                    <td>{pkg.package_name ?? '—'}</td>
                    <td>
                      <span className={`chip chip--${pkg.status}`}>
                        {t(`status.${pkg.status}`, pkg.status)}
                      </span>
                    </td>
                    <td className="table__numeric numeric">
                      {formatMoney(pkg.package_budget, i18n.language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="figure">
      {/* Figure first, label under it — the same order as the portfolio's totals strip. The
          number is what is being looked for; the label only says which number it is. */}
      <p className="figure__value numeric">{value}</p>
      <p className="figure__label">{label}</p>
    </div>
  )
}
